import { hasRealValue } from "./placeholderCheck";
import { resolvePublicAssetUrl } from "../api";

const detailsOf = (p) => p?.details || {};

/** Unwrap `{ project: {...} }` rows from hot-localities / list APIs */
export function unwrapProjectRecord(item) {
  if (!item || typeof item !== "object") return item;
  if (item.project && typeof item.project === "object") return item.project;
  return item;
}

function pickFirstMediaFromNode(node) {
  if (node == null) return null;
  if (typeof node === "string" && node.trim()) return node.trim();
  if (typeof node !== "object") return null;
  for (const k of ["image_url", "url", "path", "src", "image", "file", "photo"]) {
    const v = node[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
}

function parseGalleryList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [t];
    } catch {
      return [t];
    }
  }
  return [];
}

function galleryFirstCover(project) {
  const p = unwrapProjectRecord(project);
  if (!p || typeof p !== "object") return null;
  const d = detailsOf(p);
  const g = p.gallery && typeof p.gallery === "object" ? p.gallery : null;
  const dg = d?.gallery && typeof d.gallery === "object" ? d.gallery : null;
  const listKeys = [
    "elevation_images",
    "cover_gallery_images",
    "cover_images",
    "current_status",
    "images",
    "photos",
  ];
  const sources = [
    p.gallery,
    p.images,
    p.photos,
    p.media,
    p.project_images,
    p.slider_images,
    d?.gallery,
    d?.images,
    d?.photos,
    d?.media,
    d?.project_images,
  ];
  for (const list of sources) {
    if (!Array.isArray(list) || !list.length) continue;
    const url = pickFirstMediaFromNode(list[0]);
    if (url) return url;
  }
  for (const galleryObj of [g, dg]) {
    if (!galleryObj) continue;
    for (const key of listKeys) {
      const arr = parseGalleryList(galleryObj[key]);
      for (const item of arr) {
        const url = pickFirstMediaFromNode(item);
        if (url) return url;
      }
    }
  }
  return null;
}

/**
 * First usable cover path from API (hot localities, home lists, PDP recommendations).
 */
export function pickProjectCoverPath(project) {
  const p = unwrapProjectRecord(project);
  if (!p || typeof p !== "object") return null;
  const d = detailsOf(p);
  const candidates = [
    p.image_url,
    p.cover_image,
    p.cover_image_url,
    p.featured_image,
    p.banner_image,
    p.hero_image,
    p.hero_image_url,
    p.thumbnail,
    p.main_image,
    p.image,
    d?.cover_image,
    d?.image_url,
    d?.featured_image,
    p.apartment_buy_detail?.cover_image,
    p.apartment_buy_detail?.image_url,
    p.apartment_rent_detail?.cover_image,
    p.apartment_rent_detail?.image_url,
    p.villa_buy_detail?.cover_image,
    p.villa_buy_detail?.image_url,
    p.villa_rent_detail?.cover_image,
    p.plot_buy_detail?.cover_image,
    p.plot_rent_detail?.cover_image,
    galleryFirstCover(p),
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") return String(c).trim();
  }
  return null;
}

export function pickProjectLogoPath(project) {
  const p = unwrapProjectRecord(project);
  if (!p || typeof p !== "object") return null;
  const d = detailsOf(p);
  const candidates = [
    p.logo,
    p.project_logo,
    d?.logo,
    p.user?.company_logo,
    p.details?.logo,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") return String(c).trim();
  }
  return null;
}

/** Absolute URL for recommendation / carousel cards */
export function resolveRecommendationAssetUrl(raw, fallback = "") {
  if (!raw || !String(raw).trim()) return fallback;
  return resolvePublicAssetUrl(String(raw).trim()) || fallback;
}

/** Normalize API type hints to apartment | villa | plot */
export function normalizeProjectPropertyType(value = "") {
  const v = String(value).toLowerCase();
  if (v.includes("villa")) return "villa";
  if (v.includes("plot") || v.includes("land")) return "plot";
  if (v.includes("apartment") || v.includes("flat")) return "apartment";
  return "";
}

/**
 * Infer listing type from a project row (hot-localities, search, home lists).
 * @returns {'apartment'|'villa'|'plot'|null}
 */
export function inferProjectPropertyType(project) {
  const p = unwrapProjectRecord(project);
  if (!p || typeof p !== "object") return null;

  const typeHint = String(
    p.property_type ||
      p.propertyType ||
      p.type ||
      p.listing_type ||
      p.project_type ||
      (typeof p.category === "object" ? p.category?.name || p.category?.slug : p.category) ||
      p.details?.property_type ||
      (typeof p.details?.category === "object"
        ? p.details.category?.name || p.details.category?.slug
        : p.details?.category) ||
      ""
  );
  const fromHint = normalizeProjectPropertyType(typeHint);
  if (fromHint) return fromHint;

  if (p.plot_buy_detail || p.plot_rent_detail) return "plot";
  if (p.villa_buy_detail || p.villa_rent_detail) return "villa";
  if (p.apartment_buy_detail || p.apartment_rent_detail) return "apartment";

  const d = detailsOf(p);
  if (d?.plot_sizes || d?.plot_sizes_range || d?.total_plots) return "plot";
  if (d?.villa_sizes || d?.no_of_villas || p.villa_configs) return "villa";
  if (d?.flat_sizes || d?.bhk_configuration) return "apartment";

  return null;
}

/** True when project belongs on the given buy-home screen (apartment / villa / plot). */
export function projectMatchesPropertyType(project, pageType) {
  if (!project || !pageType) return false;
  const page = normalizeProjectPropertyType(pageType);
  if (!page) return false;

  const p = unwrapProjectRecord(project);
  const typeHint = String(
    p?.type ||
      p?.property_type ||
      p?.propertyType ||
      p?.listing_type ||
      p?.project_type ||
      (typeof p?.category === "object" ? p?.category?.name || p?.category?.slug : p?.category) ||
      ""
  ).toLowerCase();

  if (typeHint) {
    if (page === "villa" && typeHint.includes("villa")) return true;
    if (page === "plot" && (typeHint.includes("plot") || typeHint.includes("land"))) return true;
    if (page === "apartment" && (typeHint.includes("apartment") || typeHint.includes("flat"))) {
      return true;
    }
  }

  if (page === "villa") {
    return !!(p?.villa_buy_detail || p?.details?.villa_sizes || p?.details?.no_of_villas);
  }
  if (page === "plot") {
    return !!(p?.plot_buy_detail || p?.details?.plot_sizes || p?.details?.total_plots);
  }
  return !projectMatchesPropertyType(p, "villa") && !projectMatchesPropertyType(p, "plot");
}

/** Keep only projects for the active property-type screen. */
export function filterProjectsByPropertyType(projects, pageType) {
  if (!Array.isArray(projects)) return [];
  return projects.filter((item) => projectMatchesPropertyType(item, pageType));
}

/**
 * Hot-localities detail: only rows returned by the API for this page type.
 * Requires slug + title; uses API property_type when present (strict match).
 */
export function filterHotLocalityProjects(projects, pageType) {
  const page = normalizeProjectPropertyType(pageType);
  if (!page || !Array.isArray(projects)) return [];

  return projects
    .map(unwrapProjectRecord)
    .filter((p) => {
      if (!p || typeof p !== "object") return false;
      if (p.is_active === false) return false;
      const slug = String(p.slug || "").trim();
      const title = String(p.title || p.project_name || "").trim();
      if (!slug || !title) return false;

      const apiType = normalizeProjectPropertyType(p.property_type || p.propertyType || "");
      if (apiType) return apiType === page;

      return projectMatchesPropertyType(p, page);
    });
}

/**
 * Location for recommendation cards — try common API shapes.
 */
export function pickLocationFromProject(project) {
  const d = detailsOf(project);
  const raw =
    project?.location ||
    project?.project_location ||
    d?.location ||
    d?.location_name ||
    project?.locality_name ||
    "";
  return String(raw).trim();
}

/**
 * BHK / configuration for apartments & villas.
 */
export function pickBhkFromProject(project, type) {
  if (type === "plot") return "";
  const d = detailsOf(project);
  const raw =
    project?.bhk ||
    d?.bhk ||
    d?.bhk_configuration ||
    d?.configurations ||
    project?.apartment_buy_detail?.bhk_configuration ||
    project?.apartment_rent_detail?.bhk_configuration ||
    project?.villa_buy_detail?.bhk_configuration ||
    project?.villa_rent_detail?.bhk_configuration ||
    project?.villa_configs ||
    "";
  return String(raw).trim();
}

/**
 * Sqft / size text for the land-icon row (apartments & villas).
 */
export function pickSqftAreaFromProject(project, type) {
  if (type === "plot") return "";
  const d = detailsOf(project);
  const raw =
    project?.bhk_size ||
    d?.flat_sizes ||
    project?.apartment_buy_detail?.flat_sizes ||
    project?.apartment_rent_detail?.flat_sizes ||
    project?.villa_buy_detail?.villa_sizes_in_sqft ||
    project?.villa_rent_detail?.villa_sizes_in_sqft ||
    project?.villa_sizes_in_sqft ||
    project?.project_size ||
    d?.project_size ||
    "";
  return String(raw).trim();
}

/** Plot area / extent (land icon row for plots). */
export function pickPlotAreaField(project) {
  const d = detailsOf(project);
  const raw =
    project?.area ||
    project?.bhk ||
    d?.project_size ||
    project?.project_size ||
    "";
  return String(raw).trim();
}

/** Plot size / yards (yards icon row). */
export function pickPlotYards(project) {
  const d = detailsOf(project);
  const raw =
    project?.yards ||
    project?.bhk_size ||
    d?.plot_sizes ||
    d?.plot_sizes_range ||
    project?.filters?.size_range_display ||
    "";
  return String(raw).trim();
}

/**
 * Detect nonsense ranges like "₹ 2 - ₹ 17" (mis-parsed min/max).
 */
function isSuspiciousTinyPriceRange(str) {
  const nums = String(str).match(/\d[\d,]*(?:\.\d+)?/g);
  if (!nums || nums.length < 2) return false;
  const a = parseFloat(nums[0].replace(/,/g, ""));
  const b = parseFloat(nums[1].replace(/,/g, ""));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  if (hi >= 1000) return false;
  return lo < 100000 && hi < 1000;
}

/** Reject placeholder prices like "₹ 16" or "₹ 23 - ₹ 31" from hot-localities list APIs. */
function isInvalidPriceDisplay(str) {
  const t = String(str).trim();
  if (!t) return true;
  if (/cr|crore|lac|lakh|\/\s*sq/i.test(t)) {
    return isSuspiciousTinyPriceRange(t);
  }
  const nums = t.match(/\d[\d,]*(?:\.\d+)?/g);
  if (!nums?.length) return true;
  const values = nums
    .map((n) => parseFloat(n.replace(/,/g, "")))
    .filter(Number.isFinite);
  if (!values.length) return true;
  const max = Math.max(...values);
  if (max < 100000) return true;
  if (values.length >= 2) return isSuspiciousTinyPriceRange(t);
  return false;
}

/**
 * Price line for recommendation cards; avoids showing garbage ranges.
 */
export function pickPriceDisplay(project) {
  const d = detailsOf(project);
  const tryString = (s) => {
    const t = String(s).trim();
    if (!hasRealValue(t)) return null;
    if (isInvalidPriceDisplay(t)) return null;
    return t;
  };

  const candidates = [
    project?.price_range,
    project?.price,
    d?.price_range,
    project?.price_from != null && project?.price_to != null
      ? `${project.price_from} - ${project.price_to}`
      : null,
  ];

  for (const c of candidates) {
    if (c == null) continue;
    const ok = tryString(c);
    if (ok) return ok;
  }

  const sp = project?.starting_price ?? project?.min_price ?? d?.min_price;
  const ep = project?.end_price ?? project?.max_price ?? d?.max_price;
  const num = (x) => {
    if (x == null) return NaN;
    if (typeof x === "number") return x;
    return parseFloat(String(x).replace(/,/g, ""));
  };
  const a = num(sp);
  const b = num(ep);
  if (Number.isFinite(a) && Number.isFinite(b) && Math.max(a, b) >= 1000) {
    if (!isSuspiciousTinyPriceRange(`${a} - ${b}`)) {
      return `₹ ${a.toLocaleString("en-IN")} - ₹ ${b.toLocaleString("en-IN")}`;
    }
  }
  if (Number.isFinite(a) && !Number.isFinite(ep) && a >= 1000) {
    return `₹ ${a.toLocaleString("en-IN")}`;
  }

  return "";
}
